import React, { useState } from 'react'
import { Button } from '@progress/kendo-react-buttons'
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs'
import { Form, Field, FormElement } from '@progress/kendo-react-form'
import { FormInput, FormNumericTextBox } from '../FormComps/FormComponents'
import { MaterialWorking } from '../../data/interfaces'
import {
  createMaterialWorking,
  updateMaterialWorking,
  deleteMaterialWorking
} from '../../data/workingsData'
import { useToast } from '../../contexts/ToastContext'
import { FaEdit, FaTrash, FaPlus } from 'react-icons/fa'
import { materialWorkingTotal, inSection } from './workingTotals'

interface MaterialWorkingsTableProps {
  materialWorkings: MaterialWorking[]
  enquiryId?: number
  quoteId?: number
  jobId?: number
  sectionId?: number
  lockEdit: boolean
  onMaterialWorkingsChange: (workings: MaterialWorking[]) => void
}

const MaterialWorkingsTable: React.FC<MaterialWorkingsTableProps> = ({
  materialWorkings,
  enquiryId,
  quoteId,
  jobId,
  sectionId,
  lockEdit,
  onMaterialWorkingsChange
}) => {
  const { showToast } = useToast()
  const [dialogVisible, setDialogVisible] = useState(false)
  const [editingItem, setEditingItem] = useState<MaterialWorking | null>(null)
  const [isLoading, setIsLoading] = useState(false)

  const items = materialWorkings.filter(w => inSection(w.sectionId, sectionId))

  const handleAdd = () => {
    setEditingItem({
      enquiryId,
      quoteId,
      jobId,
      sectionId,
      description: '',
      netCost: 0,
      markUpPercent: 0,
      total: 0
    })
    setDialogVisible(true)
  }

  const handleEdit = (item: MaterialWorking) => {
    setEditingItem({ ...item })
    setDialogVisible(true)
  }

  const handleDelete = async (item: MaterialWorking) => {
    if (!item.materialWorkingId) return
    if (window.confirm('Are you sure you want to delete this material working?')) {
      setIsLoading(true)
      try {
        await deleteMaterialWorking(item.materialWorkingId)
        onMaterialWorkingsChange(materialWorkings.filter(w => w.materialWorkingId !== item.materialWorkingId))
        showToast('Material working deleted successfully', 'success')
      } catch (error) {
        console.error('Error deleting material working:', error)
        showToast('Error deleting material working', 'error')
      } finally {
        setIsLoading(false)
      }
    }
  }

  const handleSave = async (formData: MaterialWorking) => {
    setIsLoading(true)
    try {
      const totalCost = formData.netCost && formData.markUpPercent
        ? formData.netCost + (formData.netCost * (formData.markUpPercent / 100))
        : formData.total || 0

      const workingData = {
        ...formData,
        materialWorkingId: editingItem?.materialWorkingId,
        enquiryId,
        quoteId,
        jobId,
        sectionId: editingItem?.sectionId,
        total: totalCost,
        totalCost: totalCost
      }

      if (editingItem?.materialWorkingId) {
        const updated = await updateMaterialWorking(editingItem.materialWorkingId, workingData)
        onMaterialWorkingsChange(materialWorkings.map(w =>
          w.materialWorkingId === editingItem.materialWorkingId ? updated : w
        ))
        showToast('Material working updated successfully', 'success')
      } else {
        const created = await createMaterialWorking(workingData)
        onMaterialWorkingsChange([...materialWorkings, created])
        showToast('Material working created successfully', 'success')
      }

      setDialogVisible(false)
      setEditingItem(null)
    } catch (error) {
      console.error('Error saving material working:', error)
      showToast('Error saving material working', 'error')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="working-type-block">
      {items.length > 0 ? (
        <table className="workings-table">
          <thead>
            <tr>
              <th>Description</th>
              <th className="num">Net Cost</th>
              <th className="num">Mark Up %</th>
              <th className="num">Total</th>
              <th className="actions">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.map(item => (
              <tr key={item.materialWorkingId}>
                <td>{item.description}</td>
                <td className="num">£{(item.netCost || 0).toFixed(2)}</td>
                <td className="num">{(item.markUpPercent || 0).toFixed(2)}%</td>
                <td className="num">£{materialWorkingTotal(item).toFixed(2)}</td>
                <td className="actions">
                  <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                    <Button size="small" onClick={() => handleEdit(item)} disabled={lockEdit || isLoading} title="Edit Material Working">
                      <FaEdit />
                    </Button>
                    <Button size="small" fillMode="outline" themeColor="error" onClick={() => handleDelete(item)} disabled={lockEdit || isLoading} title="Delete Material Working">
                      <FaTrash />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="working-section-empty">No material workings yet.</div>
      )}

      {!lockEdit && (
        <div className="working-section-add">
          <Button size="small" themeColor="primary" fillMode="outline" onClick={handleAdd} disabled={isLoading}>
            <FaPlus style={{ marginRight: '0.35rem' }} /> Add Material Working
          </Button>
        </div>
      )}

      {dialogVisible && editingItem && (
        <Dialog
          title={editingItem?.materialWorkingId ? 'Edit Material Working' : 'Add Material Working'}
          onClose={() => { setDialogVisible(false); setEditingItem(null) }}
          width={500}
        >
          <Form
            onSubmit={handleSave}
            initialValues={{
              description: editingItem.description || '',
              netCost: editingItem.netCost || 0,
              markUpPercent: editingItem.markUpPercent || 0,
              total: editingItem.total || 0
            }}
            key={`material-${editingItem?.materialWorkingId || 'new'}-${Date.now()}`}
            render={(formRenderProps) => (
              <FormElement style={{ maxWidth: 650 }}>
                <Field name="description" component={FormInput} label="Description" required />
                <Field name="netCost" component={FormNumericTextBox} label="Net Cost (£)" format="n2" min={0} max={100000} step={0.01} required />
                <Field name="markUpPercent" component={FormNumericTextBox} label="Mark Up Percentage" format="n2" min={0} max={100} step={0.01} required />
                <Field name="total" component={FormNumericTextBox} label="Total (£) - Optional, will be calculated if not provided" format="n2" min={0} max={100000} step={0.01} />
                <DialogActionsBar>
                  <Button type="submit" themeColor="primary" disabled={!formRenderProps.allowSubmit || isLoading}>
                    {isLoading ? 'Saving...' : 'Save'}
                  </Button>
                  <Button onClick={() => { setDialogVisible(false); setEditingItem(null) }} disabled={isLoading}>
                    Cancel
                  </Button>
                </DialogActionsBar>
              </FormElement>
            )}
          />
        </Dialog>
      )}
    </div>
  )
}

export default MaterialWorkingsTable
